import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Alert,
  Chip,
  Stack,
  Skeleton,
  IconButton,
  Modal,
  Fade,
  Backdrop,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Event as EventIcon,
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  AccessTime as TimeIcon,
  Whatshot as FireIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  PlayCircle as PlayCircleIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { backend_url } from "../App";
import LatestEvents from "./LatestEvents";
import { fetchFIRMSData, checkFireVerification } from "../utils/firmsUtils";

// Import logos
import forest_fire_new_logo from "../images/forest_fire_new_logo.jpeg";
import fireLogo from "../images/firelogo.jpeg";

const MotionListItem = motion(Box);

function EventList({ eventClick }) {
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGifUrl, setSelectedGifUrl] = useState(null);
  const [fireSpots, setFireSpots] = useState([]);
  const [verifiedEvents, setVerifiedEvents] = useState({});

  // Get current user from localStorage
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isPunjabAdmin = user?.username === "punjabadmin";

  // Filter states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const filterOpen = Boolean(filterAnchorEl);

  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Token ${localStorage.getItem("token")}`,
      },
    }),
    [],
  );

  // Fetch FIRMS data on component mount
  useEffect(() => {
    const loadFIRMSData = async () => {
      const spots = await fetchFIRMSData();
      setFireSpots(spots);
    };
    loadFIRMSData();
  }, []);

  // Apply filter function
  const applyFilter = useMemo(() => {
    return (events, filter) => {
      if (!events) return;

      if (filter === "all") {
        setFilteredData(events);
        return;
      }

      const hours = parseInt(filter);
      const now = new Date();
      const filtered = events.filter((event) => {
        if (!event.date) return false;

        // Parse date in format "DD-MM-YYYY HH:MM:SS"
        try {
          const parts = event.date.split(" ");
          const dateParts = parts[0].split("-");
          const timeParts = parts[1] ? parts[1].split(":") : [0, 0, 0];

          const year = parseInt(dateParts[2]);
          const month = parseInt(dateParts[1]) - 1;
          const day = parseInt(dateParts[0]);
          const hour = parseInt(timeParts[0]);
          const minute = parseInt(timeParts[1]);
          const second = parseInt(timeParts[2]);

          const eventDate = new Date(year, month, day, hour, minute, second);
          const diffHours = (now - eventDate) / (1000 * 60 * 60);
          return diffHours <= hours;
        } catch {
          return false;
        }
      });

      setFilteredData(filtered);
    };
  }, []);

  useEffect(() => {
    fetch(`${backend_url}/core/api/event/`, config)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        return response.json();
      })
      .then((actualData) => {
        const events = actualData.results || [];
        setData(events);
        applyFilter(events, selectedFilter);
        setError(null);
      })
      .catch((err) => {
        console.log(err);
        setError(err.message);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [config, selectedFilter, applyFilter]);

  // Handle filter change
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (filter) => {
    if (filter) {
      setSelectedFilter(filter);
      applyFilter(data, filter);
    }
    setFilterAnchorEl(null);
  };

  const handleEventClick = (event) => {
    if (eventClick) {
      eventClick(event);
    }
    setSelectedEvent(event);

    // Set the GIF URL for modal
    let gifUrl = event.file;
    if (gifUrl && gifUrl.includes("http://127.0.0.1:8000")) {
      gifUrl = gifUrl.replace(
        "http://127.0.0.1:8000",
        "https://api.forestwatch.org.pk",
      );
    }
    setSelectedGifUrl(gifUrl);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
    setSelectedGifUrl(null);
  };

  const handleEventDetailsClick = (e, event) => {
    e.stopPropagation();
    window.location.href = `/live/${event.camera}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    try {
      const parts = dateString.split(" ");
      const dateParts = parts[0].split("-");
      const timeParts = parts[1] ? parts[1].split(":") : [0, 0, 0];

      const year = parseInt(dateParts[2]);
      const month = parseInt(dateParts[1]) - 1;
      const day = parseInt(dateParts[0]);
      const hour = parseInt(timeParts[0]);
      const minute = parseInt(timeParts[1]);
      const second = parseInt(timeParts[2]);

      const date = new Date(year, month, day, hour, minute, second);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const filterOptions = [
    { label: "All Events", value: "all" },
    { label: "Last 6 Hours", value: "6" },
    { label: "Last 12 Hours", value: "12" },
    { label: "Last 18 Hours", value: "18" },
    { label: "Last 24 Hours", value: "24" },
    { label: "Last 48 Hours", value: "48" },
  ];

  const getFilterLabel = () => {
    const option = filterOptions.find((opt) => opt.value === selectedFilter);
    return option ? option.label : "All Events";
  };

  // Fetch camera coordinates for verification
  const fetchCameraCoordinates = useCallback(
    async (cameraId) => {
      try {
        const response = await fetch(
          `${backend_url}/core/api/camera/${cameraId}/`,
          {
            headers: config.headers,
          },
        );
        if (response.ok) {
          const data = await response.json();
          return {
            lat: data.latitude,
            lng: data.longitude,
          };
        }
        return null;
      } catch (error) {
        console.error("Error fetching camera coordinates:", error);
        return null;
      }
    },
    [config.headers],
  );

  // Check if an event is verified by FIRMS
  const checkEventVerification = useCallback(
    async (event) => {
      if (!event || !event.camera || fireSpots.length === 0) return false;

      // Check if the event has fire or smoke
      const species = event.species || [];
      const hasFireOrSmoke = species.some(
        (s) =>
          s.id === "fire" ||
          s.id === "smoke" ||
          s.name?.toLowerCase() === "fire" ||
          s.name?.toLowerCase() === "smoke",
      );

      if (!hasFireOrSmoke) return false;

      // Get camera coordinates
      const coords = await fetchCameraCoordinates(event.camera);
      if (!coords) return false;

      // Check if any FIRMS spot is within 30km
      return checkFireVerification(coords.lat, coords.lng, fireSpots, 30);
    },
    [fireSpots, fetchCameraCoordinates],
  );

  // Verify all events when data or fireSpots change
  useEffect(() => {
    const verifyAllEvents = async () => {
      if (
        !filteredData ||
        filteredData.length === 0 ||
        fireSpots.length === 0
      ) {
        return;
      }

      const verificationResults = {};

      // Process events in batches to avoid overwhelming the API
      for (const event of filteredData) {
        const isVerified = await checkEventVerification(event);
        verificationResults[event.uuid] = isVerified;
      }

      setVerifiedEvents(verificationResults);
    };

    verifyAllEvents();
  }, [filteredData, fireSpots, checkEventVerification]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={100}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error loading events: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header with Filter */}
        <Box
          sx={{
            p: 2,
            background:
              "linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.98) 100%)",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {/* First Row - Title with Event Count */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <EventIcon sx={{ color: "#ffd93d" }} />
              Latest Events
              {filteredData && filteredData.length > 0 && (
                <Chip
                  label={filteredData.length}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontWeight: 600,
                    height: 20,
                    fontSize: "0.7rem",
                  }}
                />
              )}
            </Typography>
          </Box>

          {/* Second Row - Filter Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Chip
              size="small"
              label={getFilterLabel()}
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: "0.7rem",
                height: 24,
              }}
            />
            <Tooltip title="Filter Events by Time">
              <IconButton
                size="small"
                onClick={handleFilterClick}
                sx={{ color: "#fff" }}
              >
                <FilterIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={filterOpen}
          onClose={() => handleFilterClose(null)}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 180,
              bgcolor: "rgba(30,30,40,0.98)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 2,
            },
          }}
        >
          {filterOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleFilterClose(option.value)}
              selected={selectedFilter === option.value}
              sx={{
                color: "#fff",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.1)",
                },
                "&.Mui-selected": {
                  bgcolor: "rgba(255,255,255,0.15)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {selectedFilter === option.value && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#ffd93d",
                    }}
                  />
                )}
                <Typography variant="body2">{option.label}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>

        {/* Events List */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
          <AnimatePresence>
            {filteredData && filteredData.length > 0 ? (
              <Stack spacing={1.5}>
                {filteredData.map((event, index) => {
                  // Get verification status from the verifiedEvents object
                  const isVerified = verifiedEvents[event.uuid] || false;

                  return (
                    <MotionListItem
                      key={event.uuid}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ scale: 1.01, x: 4 }}
                      whileTap={{ scale: 0.99 }}
                      sx={{
                        cursor: "pointer",
                        position: "relative",
                      }}
                      onClick={() => handleEventClick(event)}
                    >
                      <Box sx={{ position: "relative" }}>
                        <LatestEvents data={event} verified={isVerified} />

                        {/* Verified by FIRMS Badge - bottom right of the card */}
                        {isVerified && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 8,
                              right: 8,
                              zIndex: 2,
                            }}
                          >
                            <Chip
                              icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                              label="Verified by FIRMS"
                              size="small"
                              sx={{
                                bgcolor: "#4caf50",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                height: 24,
                                "& .MuiChip-icon": {
                                  color: "white",
                                },
                              }}
                            />
                          </Box>
                        )}

                        {/* Event Details Icon Button - Top Right */}
                        <Tooltip title="Event Details" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => handleEventDetailsClick(e, event)}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              bgcolor: "rgba(0,0,0,0.6)",
                              backdropFilter: "blur(5px)",
                              color: "#fff",
                              "&:hover": {
                                bgcolor: "rgba(255,255,255,0.2)",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                              zIndex: 1,
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </MotionListItem>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <EventIcon
                  sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)", mb: 2 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.5)" }}
                >
                  No events found for this time range
                </Typography>
                <Button
                  size="small"
                  onClick={() => handleFilterClose("all")}
                  sx={{ mt: 2, color: "#ffd93d" }}
                >
                  Show All Events
                </Button>
              </Box>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Fullscreen Modal for GIF */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backgroundColor: "rgba(0,0,0,0.9)",
              backdropFilter: "blur(10px)",
            },
          },
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: 1200,
              maxHeight: "90vh",
              bgcolor: "rgba(20,20,30,0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: 4,
              boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background:
                  "linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.98) 100%)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Logo - conditional based on user */}
                {isPunjabAdmin ? (
                  <Box
                    component="img"
                    src={forest_fire_new_logo}
                    alt="Forestry & Wildlife Punjab"
                    sx={{
                      height: 48,
                      width: "auto",
                      maxWidth: 200,
                      objectFit: "contain",
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={fireLogo}
                    alt="LUMS Logo"
                    sx={{
                      height: 48,
                      width: "auto",
                      maxWidth: 48,
                      objectFit: "contain",
                      borderRadius: 1,
                    }}
                  />
                )}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#fff" }}
                  >
                    {selectedEvent?.camera_name || `Event Details`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.5)" }}
                  >
                    📅 {formatDate(selectedEvent?.date)}
                  </Typography>
                </Box>
                {/* Event Type Badge */}
                {selectedEvent?.species && selectedEvent.species.length > 0 && (
                  <Chip
                    size="small"
                    icon={<FireIcon sx={{ fontSize: 14 }} />}
                    label={selectedEvent.species
                      .map((s) => s.name || s.id)
                      .join(", ")}
                    sx={{
                      bgcolor: "rgba(255,107,107,0.2)",
                      color: "#ff6b6b",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="View Event Details">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<InfoIcon />}
                    onClick={() => {
                      if (selectedEvent) {
                        window.location.href = `/live/${selectedEvent.camera}`;
                      }
                    }}
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.3)",
                      "&:hover": {
                        borderColor: "#ffd93d",
                        bgcolor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Details
                  </Button>
                </Tooltip>
                <IconButton onClick={handleCloseModal} sx={{ color: "#fff" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Modal Content - GIF Player */}
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
              }}
            >
              {selectedGifUrl ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={selectedGifUrl}
                    alt="Event GIF"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                      borderRadius: 16,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      const parent = e.target.parentElement;
                      if (parent) {
                        const errorMsg = document.createElement("div");
                        errorMsg.style.cssText = `
                          color: rgba(255,255,255,0.7);
                          text-align: center;
                          padding: 20px;
                        `;
                        errorMsg.innerHTML = `
                          <span style="font-size: 48px;">🎬</span>
                          <p style="margin-top: 10px;">GIF preview not available</p>
                        `;
                        parent.appendChild(errorMsg);
                      }
                    }}
                  />
                </motion.div>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    No media available for this event
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Event Info Footer */}
            {selectedEvent && (
              <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  bgcolor: "rgba(0,0,0,0.3)",
                }}
              >
                <Chip
                  icon={<CameraIcon />}
                  label={`Camera: ${selectedEvent.camera_name || selectedEvent.camera}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#fff" }}
                />
                <Chip
                  icon={<TimeIcon />}
                  label={`Detected: ${formatDate(selectedEvent.date)}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#fff" }}
                />
                {selectedEvent.species && selectedEvent.species.length > 0 && (
                  <Chip
                    icon={<FireIcon />}
                    label={`Species: ${selectedEvent.species.map((s) => s.name || s.id).join(", ")}`}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#fff" }}
                  />
                )}
                {selectedEvent.file && (
                  <Tooltip title="Open GIF in new tab">
                    <Button
                      size="small"
                      component="a"
                      href={selectedGifUrl}
                      target="_blank"
                      startIcon={<PlayCircleIcon />}
                      sx={{
                        color: "#ffd93d",
                        "&:hover": {
                          bgcolor: "rgba(255,217,61,0.1)",
                        },
                      }}
                    >
                      Open GIF
                    </Button>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

export default EventList;
